import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, UserPlus, Copy, Check } from "lucide-react";

const TIERS = [
  { id: "one_on_one", label: "1-on-1 (full)" },
  { id: "advance", label: "Advance" },
  { id: "accountability", label: "Accountability" },
  { id: "basic", label: "Basic" },
];

const SCOPES = [
  { id: "full", label: "Full Access", hint: "Workouts + Meals + Progress + Billing (per tier)" },
  { id: "workouts_only", label: "Workouts Only", hint: "Hide meal-plan tools" },
  { id: "meals_only", label: "Meals Only", hint: "Hide workout tools" },
];

const DURATIONS = [
  { id: 30, label: "30 Days" },
  { id: 90, label: "90 Days" },
  { id: 180, label: "6 Months" },
  { id: 365, label: "1 Year" },
  { id: 0, label: "Lifetime / No Expiry" },
];

export default function AddMemberModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("one_on_one");
  const [scope, setScope] = useState("full");
  const [activateNow, setActivateNow] = useState(true);
  const [duration, setDuration] = useState(30); // 0 = lifetime
  const [weeklyEmail, setWeeklyEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        tier,
        access_scope: scope,
        activate_immediately: activateNow,
        duration_days: activateNow && duration > 0 ? duration : null,
        weekly_email_opt_in: weeklyEmail,
      };
      const r = await api.post("/admin/members/manual-create", payload);
      setResult(r.data);
      toast.success("Member created · activation email sent");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create member");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.activation_link);
      setCopied(true);
      toast.success("Activation link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the URL manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="add-member-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full sm:max-w-xl border border-[#E5B43A] bg-[#0F0F0F] shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5B43A]/40 bg-[#E5B43A]/10 flex items-center gap-3">
          <UserPlus className="text-[#E5B43A] shrink-0" size={18} />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] font-bold flex-1">Add Member · Manual</div>
          <button onClick={onClose} disabled={busy} className="text-zinc-400 hover:text-white" data-testid="add-member-close">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit}>
            <div className="px-6 py-6 space-y-5">
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Name</label>
                <input
                  type="text" autoFocus value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full Name"
                  data-testid="add-member-name"
                  className="w-full bg-[#0A0A0A] border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:border-[#E5B43A]"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Email</label>
                <input
                  type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="athlete@example.com"
                  data-testid="add-member-email"
                  className="w-full bg-[#0A0A0A] border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:border-[#E5B43A]"
                />
                <div className="text-[10px] tracking-widest uppercase text-zinc-600 mt-1">Activation link sent here</div>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Tier</label>
                <div className="grid grid-cols-2 gap-2">
                  {TIERS.map((t) => (
                    <button
                      key={t.id} type="button"
                      onClick={() => setTier(t.id)}
                      data-testid={`add-member-tier-${t.id}`}
                      className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${tier === t.id ? "border-[#E5B43A] bg-[#E5B43A] text-black" : "border-zinc-700 text-zinc-300 hover:border-[#E5B43A]"}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Access Scope</label>
                <div className="space-y-2">
                  {SCOPES.map((s) => (
                    <button
                      key={s.id} type="button"
                      onClick={() => setScope(s.id)}
                      data-testid={`add-member-scope-${s.id}`}
                      className={`w-full text-left px-4 py-3 border transition-colors ${scope === s.id ? "border-[#E5B43A] bg-[#E5B43A]/10" : "border-zinc-800 hover:border-zinc-600"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full border ${scope === s.id ? "bg-[#E5B43A] border-[#E5B43A]" : "border-zinc-600"}`} />
                        <span className="font-bold text-sm">{s.label}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1 ml-5">{s.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox" checked={activateNow}
                    onChange={(e) => setActivateNow(e.target.checked)}
                    data-testid="add-member-activate-now"
                    className="w-4 h-4 accent-[#E5B43A]"
                  />
                  <span className="text-sm">Activate subscription immediately</span>
                </label>
                <div className="text-[11px] text-zinc-500 ml-7">If off, the account is created but they won&apos;t have access until you grant a tier.</div>
                {activateNow && (
                  <div className="mt-3 ml-7">
                    <label className="block text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Duration</label>
                    <div className="flex flex-wrap gap-2">
                      {DURATIONS.map((d) => (
                        <button
                          key={d.id} type="button"
                          onClick={() => setDuration(d.id)}
                          data-testid={`add-member-duration-${d.id === 0 ? "lifetime" : d.id}`}
                          className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border transition-colors ${duration === d.id ? "border-[#E5B43A] bg-[#E5B43A] text-black" : "border-zinc-700 text-zinc-300 hover:border-[#E5B43A]"}`}
                        >{d.label}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox" checked={weeklyEmail}
                    onChange={(e) => setWeeklyEmail(e.target.checked)}
                    data-testid="add-member-weekly-email"
                    className="w-4 h-4 accent-[#E5B43A]"
                  />
                  <span className="text-sm">Send weekly Sunday-night packet (AI 7-day plan PDF)</span>
                </label>
                <div className="text-[11px] text-zinc-500 ml-7">Member can toggle this off from their dashboard anytime.</div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-zinc-800 bg-[#0A0A0A] flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button type="button" onClick={onClose} disabled={busy} className="px-5 py-3 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={busy} data-testid="add-member-submit" className="px-5 py-3 text-xs font-bold uppercase tracking-widest bg-[#E5B43A] text-black hover:bg-white transition-colors disabled:opacity-60">
                {busy ? "Creating..." : "Create & Send Activation"}
              </button>
            </div>
          </form>
        ) : (
          <div className="px-6 py-6 space-y-5" data-testid="add-member-success">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400 mb-2">✓ Member Created</div>
              <div className="font-display text-xl">{result.email}</div>
            </div>
            <div className="border border-zinc-800 bg-[#141414] px-4 py-3 text-sm text-zinc-300">
              Activation email was sent. If the email doesn&apos;t arrive (or you want to text/DM them the link), copy it here:
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Activation Link</label>
              <div className="flex gap-2">
                <input
                  readOnly value={result.activation_link}
                  data-testid="add-member-activation-link"
                  className="flex-1 bg-[#0A0A0A] border border-zinc-800 px-3 py-2 text-zinc-200 text-xs font-mono"
                />
                <button
                  onClick={copyLink}
                  data-testid="add-member-copy-link"
                  className="px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-[#E5B43A] hover:text-[#E5B43A]"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-600 mt-2">
                Expires {new Date(result.expires_at).toLocaleString()}
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} data-testid="add-member-done" className="px-5 py-3 text-xs font-bold uppercase tracking-widest bg-[#E5B43A] text-black hover:bg-white transition-colors">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
